import React, { useEffect, useState } from "react";
import { Empty } from "./OverviewTab";
import { useT } from "../../lib/i18n";
import { api } from "../../lib/api";
import { WarningOctagon, CheckCircle, FloppyDisk } from "@phosphor-icons/react";

const STATUSES = ["open", "in_progress", "closed"];

export default function GapsTab({ version, project, deviceId, onChange }) {
  const t = useT();
  const [traceRecords, setTraceRecords] = useState([]);
  useEffect(() => {
    if (!project?.id) return;
    api.get(`/projects/${project.id}/trace`, { params: deviceId ? { device_id: deviceId } : {} })
      .then(({ data }) => setTraceRecords(data || []))
      .catch(() => setTraceRecords([]));
  }, [project?.id, deviceId]);
  if (!version) return <Empty msg={t("ov.no_analysis")} />;
  const gaps = version.compliance_gaps || [];
  const high = gaps.filter(g => g.severity === "high");
  const med = gaps.filter(g => g.severity !== "high");
  const closed = gaps.filter(g => g.capa?.status === "closed").length;

  return (
    <div className="space-y-4" data-testid="gaps-list">
      <div className="grid grid-cols-3 gap-4">
        <div className="grid-card p-5"><div className="text-xs font-mono uppercase text-zinc-500">High-severity</div><div className="font-mono text-3xl font-bold text-red-600 mt-2">{high.length}</div></div>
        <div className="grid-card p-5"><div className="text-xs font-mono uppercase text-zinc-500">Medium / low</div><div className="font-mono text-3xl font-bold text-amber-600 mt-2">{med.length}</div></div>
        <div className="grid-card p-5"><div className="text-xs font-mono uppercase text-zinc-500">{t("capa.closed")}</div><div className="font-mono text-3xl font-bold text-emerald-600 mt-2">{closed}/{gaps.length}</div></div>
      </div>

      {gaps.length === 0 ? (
        <div className="grid-card p-10 text-center text-emerald-600">
          <CheckCircle size={36} weight="fill" className="mx-auto" />
          <div className="mt-3 text-sm font-semibold">No compliance gaps detected.</div>
        </div>
      ) : (
        <div className="space-y-3">
          {gaps.map((g, i) => <GapRow key={`${g.topic || "gap"}-${i}`} gap={g} index={i} project={project} version={version} deviceId={deviceId} traceRecords={traceRecords} onChange={onChange} />)}
        </div>
      )}
    </div>
  );
}

function GapRow({ gap, index, project, version, deviceId, traceRecords, onChange }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [capa, setCapa] = useState(gap.capa || { owner: "", due_date: "", status: "open", evidence: "", trace_record_id: "" });
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      await api.put(
        `/projects/${project.id}/versions/${version.version}/gaps/${index}/capa`,
        capa,
        { params: deviceId ? { device_id: deviceId } : {} },
      );
      onChange?.();
      setOpen(false);
    } finally { setSaving(false); }
  }

  const statusColor = capa.status === "closed" ? "text-emerald-700 border-emerald-300 bg-emerald-50"
                    : capa.status === "in_progress" ? "text-blue-700 border-blue-300 bg-blue-50"
                    : "text-amber-700 border-amber-300 bg-amber-50";

  return (
    <div className="grid-card p-4" data-testid={`gap-${index}`}>
      <div className="flex items-start gap-3">
        <span className={`chip ${gap.severity === "high" ? "chip-III" : "chip-IIb"} shrink-0`}>
          <WarningOctagon size={10} weight="fill" />&nbsp;{gap.severity}
        </span>
        <div className="flex-1">
          <div className="text-sm font-semibold">{gap.topic}</div>
          <div className="text-xs text-zinc-600 mt-1">{gap.issue}</div>
          <div className="text-[10px] font-mono text-zinc-400 mt-1">{gap.mdr_article || "—"}</div>
        </div>
        <button onClick={() => setOpen(!open)} data-testid={`gap-toggle-${index}`}
          className={`text-xs font-mono border px-2 py-1 rounded-sm ${statusColor}`}>
          {t(`capa.${capa.status || "open"}`)}
        </button>
      </div>

      {open && (
        <div className="mt-4 pt-4 border-t border-zinc-100 grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label={t("capa.owner")} value={capa.owner} onChange={(v) => setCapa({ ...capa, owner: v })} testid={`capa-owner-${index}`} />
          <Field label={t("capa.due")} type="date" value={capa.due_date} onChange={(v) => setCapa({ ...capa, due_date: v })} testid={`capa-due-${index}`} />
          <div>
            <label className="text-xs font-semibold uppercase text-zinc-600">{t("capa.status")}</label>
            <select value={capa.status} onChange={(e) => setCapa({ ...capa, status: e.target.value })} data-testid={`capa-status-${index}`}
              className="mt-1 w-full border border-zinc-200 px-2 py-1.5 rounded-sm text-sm bg-white focus:border-blue-600 focus:outline-none">
              {STATUSES.map(s => <option key={s} value={s}>{t(`capa.${s}`)}</option>)}
            </select>
          </div>
          <Field label={t("capa.evidence")} value={capa.evidence} onChange={(v) => setCapa({ ...capa, evidence: v })} testid={`capa-ev-${index}`} />
          <div className="md:col-span-2">
            <label className="text-xs font-semibold uppercase text-[var(--text-2)]" htmlFor={`capa-lot-${index}`}>{t("trace.link_lot")}</label>
            <select
              id={`capa-lot-${index}`}
              value={capa.trace_record_id || ""}
              onChange={(event) => setCapa({ ...capa, trace_record_id: event.target.value || null })}
              data-testid={`capa-lot-${index}`}
              className="mt-1 w-full rounded-sm border border-[var(--border)] bg-[var(--surface)] px-2 py-1.5 text-sm text-[var(--text)]"
            >
              <option value="">{t("trace.no_linked_lot")}</option>
              {traceRecords.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.product}{item.variant_name ? ` · ${item.variant_name}` : ""}{item.batch_lot ? ` · ${t("trace.batch")}: ${item.batch_lot}` : ""}{item.ref ? ` · ${item.ref}` : ""}
                </option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2 flex justify-end">
            <button onClick={save} disabled={saving} data-testid={`capa-save-${index}`} className="btn-primary inline-flex items-center gap-2 px-3 py-1.5 text-xs">
              <FloppyDisk size={12} weight="bold" /> {saving ? "…" : t("capa.save")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase text-zinc-600">{label}</label>
      <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="mt-1 w-full border border-zinc-200 px-2 py-1.5 rounded-sm text-sm focus:border-blue-600 focus:outline-none" />
    </div>
  );
}
